import { css, FlattenSimpleInterpolation } from 'styled-components'
import { IGridConfig } from '../common/models/grid'

interface ISizes {
  [index: string]: number
}

const sizes: ISizes = {
  S: 767,
  S2: 996,
  S3: 1149,
  M: 1279,
  L: 1439,
  XL: 1639,
}

export enum Breakpoint {
  S = sizes.S,
  M = sizes.M,
  L = sizes.L,
}

export const gridConfig: IGridConfig = {
  breakpoints: [
    {
      name: 's',
      startsFrom: 0,
      gutterWidth: 0,
      maxColumns: 4,
    },
    {
      name: 'm',
      startsFrom: Breakpoint.S,
      gutterWidth: 0,
      maxColumns: 8,
    },
    {
      name: 'l',
      startsFrom: Breakpoint.M,
      gutterWidth: 0,
      maxColumns: 16,
    },
  ],
}

// iterate through the sizes and create a media template
const mqFrom = Object.keys(sizes).reduce(
  (mqObject, breakPointLabel) => {
    // use em in breakpoints to work properly cross-browser and support users
    // changing their browsers font-size: https://zellwk.com/blog/media-query-units/
    // const emSize = sizes[breakPointLabel] / 16

    mqObject[breakPointLabel] = (
      cssStrings: TemplateStringsArray,
      breakpointSize: string
    ) => css`
      @media (min-width: ${sizes[breakPointLabel]}px) {
        ${css(cssStrings, breakpointSize)}
      }
    `
    return mqObject
  },
  {
    px:
      (px: number) =>
      (cssStrings: TemplateStringsArray, breakpointSize: string) =>
        css`
          @media (min-width: ${px}px) {
            ${css(cssStrings, breakpointSize)}
          }
        `,
  } as { [key: string]: any }
)

// iterate through the sizes and create a media template
const mqTo = Object.keys(sizes).reduce(
  (mqObject, breakPointLabel) => {
    // use em in breakpoints to work properly cross-browser and support users
    // changing their browsers font-size: https://zellwk.com/blog/media-query-units/
    const emSize = (sizes[breakPointLabel] - 1) / 16

    mqObject[breakPointLabel] = (
      cssStrings: TemplateStringsArray,
      breakpointSize: string
    ) => css`
      @media (max-width: ${emSize}em) {
        ${css(cssStrings, breakpointSize)};
      }
    `
    return mqObject
  },
  {
    px:
      (px: number) =>
      (cssStrings: TemplateStringsArray, breakpointSize: string) =>
        css`
          @media (max-width: ${px}px) {
            ${css(cssStrings, breakpointSize)};
          }
        `,
  } as { [key in keyof ISizes]: any }
)

export interface IScreenRange {
  from?: Breakpoint
  to?: Breakpoint
}

export interface IScreenRanges {
  s: IScreenRange
  m: IScreenRange
  l: IScreenRange
}

// A screen range defines a range throughout which the styles stay the same
export const screenRanges: IScreenRanges = {
  s: { from: 0, to: Breakpoint.S },
  m: { from: Breakpoint.S, to: Breakpoint.M },
  l: { from: Breakpoint.M, to: Breakpoint.L },
}

/**
 * Create the needed CSS for a given media query (works like a mixin)
 * @param range The range to use (passed styles will only be applied if screen size corresponds to this range)
 * @param styles The styles to conditionally apply
 */
const applyResponsive = (
  range: IScreenRange,
  styles: FlattenSimpleInterpolation | string
) => {
  let mediaQuery = '@media screen'
  // Conditionally build the media query declaration, based on the set limits
  if (range.from && range.to) {
    mediaQuery += ` and
      (min-width: ${range.from / 16}em) and
      (max-width: ${range.to / 16}em)`
  } else if (range.from && !range.to) {
    mediaQuery += ` and
      (min-width: ${range.from / 16}em)`
  } else if (!range.from && range.to) {
    mediaQuery += ` and
      (max-width: ${range.to / 16}em)`
  }

  return css`
    ${mediaQuery} {
      ${styles}
    }
  `
}

export { sizes, mqFrom, mqTo, applyResponsive }
